import dayjs from 'dayjs'
import { groupBy } from 'lodash-es'
import { KOLAnalyze } from '@/api/workspaces-api'
import { Granularity } from '@/components/kol/common/time-granularity-selector'
import { DEFAULT_DATE_FORMAT } from '@/constants/date-format'

function formatFunction(publishTime: string, granularity: string): number {
  if (granularity === 'day') {
    return dayjs(publishTime).dayOfYear()
  } else if (granularity === 'week') {
    return dayjs(publishTime).week()
  } else {
    return dayjs(publishTime).month()
  }
}

function groupTimeData(
  timeData: MergedKOLAnalyze[],
  timeGranularity: Granularity,
): (MergedKOLAnalyze & { formatTime: number })[][] {
  const formatData = timeData.map((d) => {
    return {
      ...d,
      formatTime: formatFunction(d.publishedAt, timeGranularity),
    }
  })
  return Object.values(groupBy(formatData, 'formatTime'))
}

export interface MergedKOLAnalyze extends KOLAnalyze {
  mockData?: boolean
}

type GroupInteractiveByGranularityReturnValue = (Pick<
  KOLAnalyze,
  | 'commentCount'
  | 'interactiveCount'
  | 'likeCount'
  | 'publishedAt'
  | 'shareCount'
> & {
  title: string
  totalAmount: number
})[]

export function groupInteractiveByGranularity(
  timeData: MergedKOLAnalyze[],
  timeGranularity: Granularity,
): GroupInteractiveByGranularityReturnValue {
  const groupData = groupTimeData(timeData, timeGranularity)

  return groupData
    .map((group) =>
      group.reduce(
        (prev, next) => {
          return {
            commentCount: prev.commentCount + next.commentCount,
            interactiveCount: prev.interactiveCount + next.interactiveCount,
            likeCount: prev.likeCount + next.likeCount,
            publishedAt: prev.publishedAt,
            title: prev.title, // 看型別的話，這個始終為 ''
            shareCount: prev.shareCount + next.shareCount,
            totalAmount: next.mockData
              ? prev.totalAmount
              : prev.totalAmount + 1,
          }
        },
        {
          commentCount: 0,
          interactiveCount: 0,
          likeCount: 0,
          publishedAt: group[0].publishedAt,
          title: '',
          shareCount: 0,
          totalAmount: 0,
        },
      ),
    )
    .sort((prev, next) => {
      return dayjs(prev.publishedAt).isAfter(next.publishedAt) ? 1 : -1
    })
}

type GroupViewByGranularityReturnValue = (Pick<
  KOLAnalyze,
  'viewCount' | 'publishedAt'
> & {
  title: string
  totalAmount: number
})[]

export function groupViewByGranularity<T extends keyof MergedKOLAnalyze>(
  timeData: MergedKOLAnalyze[],
  timeGranularity: Granularity,
  type: T,
  typeValue: boolean,
): GroupViewByGranularityReturnValue {
  const groupData = groupTimeData(timeData, timeGranularity)

  return groupData
    .map((group) =>
      group.reduce(
        (prev, next) => {
          const nextData =
            next.isVideo && next[type] === typeValue
              ? next
              : { viewCount: 0, mockData: true }

          return {
            viewCount: prev.viewCount + nextData.viewCount,
            [type]: typeValue,
            title: prev.title,
            publishedAt: prev.publishedAt,
            totalAmount:
              'mockData' in nextData && nextData.mockData
                ? prev.totalAmount
                : prev.totalAmount + 1,
          }
        },
        {
          [type]: typeValue,
          publishedAt: group[0].publishedAt,
          title: '',
          viewCount: 0,
          totalAmount: 0,
        },
      ),
    )
    .sort((prev, next) => {
      return dayjs(prev.publishedAt).isAfter(next.publishedAt) ? 1 : -1
    })
}

interface PostDrawData
  extends Pick<
    KOLAnalyze,
    'isLive' | 'isVideo' | 'publishedAt' | 'interactiveCount' | 'viewCount'
  > {
  amount: number
}

export type GroupByPostType = 'post' | 'video' | 'live'

const maskPostByType = (
  post: ReturnType<typeof groupTimeData>[number][number],
  checker: GroupByPostType,
):
  | ReturnType<typeof groupTimeData>[number][number]
  | {
      interactiveCount: 0
      viewCount: 0
      mockData: true
    } => {
  if (
    (checker === 'post' && !post.isVideo) ||
    (checker === 'video' && post.isVideo && !post.isLive) ||
    (checker === 'live' && post.isVideo && post.isLive)
  ) {
    return post
  } else {
    return {
      interactiveCount: 0,
      viewCount: 0,
      mockData: true,
    }
  }
}

export function groupPostByGranularity(
  timeData: MergedKOLAnalyze[],
  timeGranularity: Granularity,
  type: GroupByPostType,
): PostDrawData[] {
  const groupData = groupTimeData(timeData, timeGranularity)

  return groupData
    .map((group) =>
      group.reduce(
        (prev, next) => {
          const nextData = maskPostByType(next, type)
          return {
            isLive: type === 'live',
            isVideo: type !== 'post',
            publishedAt: prev.publishedAt,
            interactiveCount: prev.interactiveCount + nextData.interactiveCount,
            viewCount: prev.viewCount + nextData.viewCount,
            amount: nextData.mockData ? prev.amount : prev.amount + 1,
          }
        },
        {
          isLive: type === 'live',
          isVideo: type !== 'post',
          publishedAt: dayjs(group[0].publishedAt).format(DEFAULT_DATE_FORMAT),
          interactiveCount: 0,
          viewCount: 0,
          amount: 0,
        },
      ),
    )
    .sort((prev, next) => {
      return dayjs(prev.publishedAt).isAfter(next.publishedAt) ? 1 : -1
    })
}

const createTimeMap = (): {
  name: string
  data: {
    x: string
    y: number
    count: number
    interactive: number
    view: number
  }[]
}[] => {
  const HourOfDay = 24
  const DayOfWeek = 7

  return Array(HourOfDay)
    .fill(null)
    .map((_hour, hourIndex) => {
      return {
        name: String(hourIndex),
        data: Array(DayOfWeek)
          .fill(null)
          .map((_day, dayIndex) => {
            return {
              x: dayIndex.toString(),
              y: 0,
              count: 0,
              interactive: 0,
              view: 0,
            }
          }),
      }
    })
}

interface ChartData {
  x: string
  y: number
}

export interface EngageData {
  count: number
  interactive: number
  view: number
}

export function groupPostByHourOfWeek(timeData: KOLAnalyze[]): {
  name: string
  data: (ChartData & EngageData)[]
}[] {
  const timeMap = createTimeMap()
  const hourOfWeekData = timeData.map((d) => {
    return {
      ...d,
      day: dayjs(d.publishedAt).day(),
      hour: dayjs(d.publishedAt).hour(),
    }
  })

  hourOfWeekData.forEach((data) => {
    const timeMapData = timeMap[data.hour]['data'][data.day]
    timeMapData.count = timeMapData.count + 1
    timeMapData.interactive = timeMapData.interactive + data.interactiveCount
    timeMapData.view = timeMapData.view + data.viewCount
  })

  return timeMap
}
